import { Fragment } from 'react';
import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import cn from 'classnames';

import { CaretDown } from 'components/svg/CaretDown';
import { useAppSelector } from 'hooks/useRedux';
import { useGetResumeDataQuery } from 'store/apis/user';

import { CAREER_LINKS, COMMON_LINKS, COMPANIES_LINKS, JOBS_LINKS } from './data';
import { CommonList, IconList, ListProps, TitleList } from './links';

import classes from './index.module.css';

const Trigger = ({ children, className, ...props }: NavigationMenu.NavigationMenuTriggerProps) => (
  <NavigationMenu.Trigger
    className={cn('z-px-10 z-py-6 z-br-4 d-flex align-items-center', classes.trigger, className)}
    {...props}
  >
    {children} <CaretDown className={cn('z-ml-6', classes.caretDown)} />
  </NavigationMenu.Trigger>
);

const Content = ({ children, className, ...props }: NavigationMenu.NavigationMenuContentProps) => (
  <NavigationMenu.Content
    className={cn('position-absolute', classes.content, className)}
    {...props}
  >
    <div className={cn('container d-grid', classes.contentContainer)}>{children}</div>
  </NavigationMenu.Content>
);

const getList = (list: ListProps) => {
  if (list?.title) {
    return <TitleList key={`links-list-nav-${list.title}`} title={list.title} links={list.links} />;
  }

  if (list?.iconList) {
    return <IconList key={`icon-list-nav-${list.title}`} links={list.links} />;
  }

  return <CommonList key={`common-list-nav-${list.title}`} links={list.links} />;
};

const Common = () => {
  const isLoggedIn = useAppSelector(({ user }) => user.isLoggedIn);
  const { data: resumeData } = useGetResumeDataQuery('resumeData', { skip: !isLoggedIn });

  const isResumeCompleted = resumeData?.isCompleted ?? false;

  const links = COMMON_LINKS({ isLoggedIn: isLoggedIn && isResumeCompleted });

  return <CommonList links={links} />;
};

export const Navigation = () => {
  const isLoggedIn = useAppSelector(({ user }) => user.isLoggedIn);

  return (
    <NavigationMenu.Root className="overflow-hidden d-none d-lg-block">
      <NavigationMenu.List className={cn('z-p-0 z-m-0 z-ml-26 d-flex', classes.list)}>
        {isLoggedIn ? (
          <NavigationMenu.Item>
            <NavigationMenu.Link className={cn('z-px-10 z-py-6 z-br-4 d-flex align-items-center', classes.trigger)} href="/matches/">
              Jobs
            </NavigationMenu.Link>
          </NavigationMenu.Item>
        ) : (
          <NavigationMenu.Item>
            <Trigger>Jobs</Trigger>
            <Content>
              {JOBS_LINKS.map(getList)}
              <Common />
            </Content>
          </NavigationMenu.Item>
        )}
        <NavigationMenu.Item>
          <Trigger>Career</Trigger>
          <Content>
            {CAREER_LINKS.map(getList)}
            <Common />
          </Content>
        </NavigationMenu.Item>
        <NavigationMenu.Item>
          <Trigger>Companies</Trigger>
          <Content>
            {COMPANIES_LINKS.map(getList)}
            <Common />
          </Content>
        </NavigationMenu.Item>
      </NavigationMenu.List>

      <div className={cn('position-absolute overflow-hidden', classes.viewportPos)}>
        <NavigationMenu.Viewport className={classes.viewport} />
      </div>
    </NavigationMenu.Root>
  );
};

const removeAllCSRLinks = (list: ListProps) => ({
  ...list,
  links: list.links.filter(({ text }) => text !== 'View All').filter(({ href }) => !href.includes('/career-quiz/')),
});

export const SEONavigation = () => {
  const list = [
    ...JOBS_LINKS.slice(0, 2),
    ...CAREER_LINKS.slice(0, 2),
    ...COMPANIES_LINKS.slice(0, 2),
    { links: COMMON_LINKS({ isLoggedIn: false }) },
  ].map(removeAllCSRLinks);
  return (
    <div className="d-none">
      {list.map((item, idx) => (
        <Fragment key={`seo-header-link-list-${item.title}-${idx}`}>
          {item?.title && <p>{item.title}</p>}
          <ul>
            {item.links.map((link, idx) => (
              <li key={`seo-header-link-${link.text}-${idx}`}>
                <a href={link.href}>{link.text}</a>
              </li>
            ))}
          </ul>
        </Fragment>
      ))}
    </div>
  );
};
